import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { ArrowLeft, LogIn, User, Lock, MessageCircle, ShieldCheck, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const RESEND_COOLDOWN_SECONDS = 30;

const SignInPage = ({ onNavigateBack, onNavigateToRegister, onSignInSuccess }) => {
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [step, setStep] = useState('password'); // password | otp
  const [pendingToken, setPendingToken] = useState('');
  const [otpMeta, setOtpMeta] = useState({ country: '', mobile: '' });
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const cooldownTimer = useRef(null);

  useEffect(() => () => cooldownTimer.current && clearInterval(cooldownTimer.current), []);

  const startCooldown = () => {
    setCooldown(RESEND_COOLDOWN_SECONDS);
    if (cooldownTimer.current) clearInterval(cooldownTimer.current);
    cooldownTimer.current = setInterval(() => {
      setCooldown((s) => {
        if (s <= 1) {
          clearInterval(cooldownTimer.current);
          cooldownTimer.current = null;
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  // Admin shortcut removed — Admin1 now goes through the same WhatsApp
  // OTP flow as regular users so the Blexbot session powering the Admin
  // Panel is established end-to-end. The backend tags admin accounts with
  // `is_admin: true` on the verify-otp response and the parent App.js
  // routes those sessions to <AdminPanel/>.

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!credentials.username || !credentials.password) {
      setError('Please enter both username and password');
      return;
    }
    setLoading(true);
    setError('');
    setStatus('');

    try {
      const { data } = await axios.post(`${API}/login`, {
        username: credentials.username,
        password: credentials.password,
      });
      if (data.step === 'otp_required') {
        setPendingToken(data.pending_token);
        setOtpMeta({ country: data.whatsapp_country_code, mobile: data.whatsapp_number });
        setStep('otp');
        setStatus('Verification code sent to your WhatsApp.');
        startCooldown();
      } else if (data.step === 'authenticated') {
        onSignInSuccess({ ...data.user, session_token: data.session_token });
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid username or password');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    if (!/^[0-9]{4,8}$/.test(code.trim())) {
      setError('Enter the numeric code you received on WhatsApp.');
      return;
    }
    setLoading(true);
    setError('');
    setStatus('');
    try {
      const { data } = await axios.post(`${API}/login/verify-otp`, {
        pending_token: pendingToken,
        code: code.trim(),
      });
      onSignInSuccess({ ...data.user, session_token: data.session_token });
    } catch (err) {
      setError(err.response?.data?.detail || 'The code is incorrect or has expired.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0 || !pendingToken) return;
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/login/resend-otp`, { pending_token: pendingToken });
      setStatus('A new verification code was sent to your WhatsApp.');
      startCooldown();
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not resend the code.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1E3A5F] via-[#2C5282] to-[#1E3A5F] page-transition">
      <div className="bg-white border-b-2 border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center space-x-6">
            <img
              src="https://customer-assets.emergentagent.com/job_dc1c0b21-5011-47e1-a19d-11de6678deed/artifacts/55cbyjpt_Logo.png"
              alt="PriceValuator Logo"
              className="h-48 object-contain"
            />
            <Button
              variant="ghost"
              onClick={onNavigateBack}
              className="text-[#1E3A5F] hover:bg-gray-100 rounded-full px-4 py-2"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md">
          <Card className="rounded-2xl border-2 border-cyan-400/30 shadow-2xl bg-white/95 backdrop-blur-lg">
            <CardHeader className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-t-2xl pb-8">
              <CardTitle className="text-3xl text-center text-[#1E3A5F] flex flex-col items-center">
                <div className="bg-cyan-100 p-4 rounded-full mb-4">
                  {step === 'otp' ? (
                    <MessageCircle className="w-8 h-8 text-cyan-600" />
                  ) : (
                    <LogIn className="w-8 h-8 text-cyan-600" />
                  )}
                </div>
                {step === 'otp' ? 'Verify with WhatsApp' : 'Sign In to PriceValuator'}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-8">
              {error && (
                <Alert className="mb-6 bg-red-50 border-red-200">
                  <AlertDescription className="text-red-800" data-testid="signin-error">{error}</AlertDescription>
                </Alert>
              )}
              <p className="sr-only" aria-live="polite" data-testid="signin-status">{status}</p>

              {step === 'password' && (
                <form onSubmit={handlePasswordSubmit} className="space-y-6" data-testid="signin-password-form">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <User className="w-4 h-4 inline mr-2" /> Username
                    </label>
                    <Input
                      data-testid="signin-username"
                      type="text"
                      value={credentials.username}
                      onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                      placeholder="Enter your username"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Lock className="w-4 h-4 inline mr-2" /> Password
                    </label>
                    <Input
                      data-testid="signin-password"
                      type="password"
                      value={credentials.password}
                      onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                      placeholder="Enter your password"
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="signin-submit"
                    className="w-full bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white rounded-full py-3 font-semibold text-lg"
                  >
                    {loading ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Sending code...</>
                    ) : (
                      <><LogIn className="w-5 h-5 mr-2" />Sign In</>
                    )}
                  </Button>
                </form>
              )}

              {step === 'otp' && (
                <form onSubmit={handleOtpSubmit} className="space-y-5" data-testid="signin-otp-form">
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                    Code sent to WhatsApp <span className="font-semibold">{otpMeta.country} {otpMeta.mobile}</span>{' '}
                    <button
                      type="button"
                      onClick={() => { setStep('password'); setCode(''); setError(''); setStatus(''); }}
                      className="text-cyan-700 underline hover:text-cyan-900 ml-1"
                      data-testid="signin-change-account"
                    >
                      Use another account
                    </button>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Verification code
                    </label>
                    <Input
                      data-testid="signin-otp"
                      type="text"
                      inputMode="numeric"
                      maxLength={8}
                      autoComplete="one-time-code"
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
                      placeholder="Enter the code from WhatsApp"
                      className="tracking-widest text-lg"
                    />
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      type="submit"
                      disabled={loading || !code}
                      data-testid="signin-otp-submit"
                      className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white rounded-full font-semibold"
                    >
                      {loading ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verifying...</>
                      ) : (
                        <><ShieldCheck className="w-4 h-4 mr-2" />Verify code</>
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleResend}
                      disabled={cooldown > 0 || loading}
                      data-testid="signin-otp-resend"
                      className="rounded-full"
                    >
                      {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
                    </Button>
                  </div>
                </form>
              )}

              <div className="mt-8 text-center">
                <p className="text-gray-600 mb-3">Don't have an account?</p>
                <Button
                  onClick={onNavigateToRegister}
                  variant="outline"
                  className="rounded-full border-cyan-400 text-cyan-600 hover:bg-cyan-50 px-6 py-2"
                >
                  Register a New Account
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SignInPage;
